import React, { useState } from 'react';
import { Plug, ArrowRight, ArrowLeft, Info, CheckCircle2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import { IntegrationPatternSelector } from '../../components/design-phase';
import type { IntegrationDesignPlan } from '../../types/designPhase';

interface Step7Props {
  caseId: string;
  caseData: any;
  onNext: () => void;
  onBack: () => void;
}

export const Step7IntegrationPattern: React.FC<Step7Props> = ({
  caseId,
  caseData,
  onNext,
  onBack,
}) => {
  const [confirmedPlan, setConfirmedPlan] = useState<IntegrationDesignPlan | null>(null);

  const handleConfirm = (plan: IntegrationDesignPlan) => {
    setConfirmedPlan(plan);
    console.log('Integration plan confirmed:', plan);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Plug className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-neutral-900">
            Step 7: Integration Pattern Selection
          </h2>
        </div>
        <p className="text-neutral-600">
          Choose the optimal integration pattern for each entity in your schema
        </p>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-900">
            <p className="font-medium mb-1">About Integration Patterns</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>Batch ETL:</strong> Scheduled bulk data loads, ideal for historical/analytics data</li>
              <li><strong>Real-time CDC:</strong> Change Data Capture for live updates, best for operational data</li>
              <li><strong>API-Based:</strong> On-demand API calls, good for reference data or event-driven</li>
              <li><strong>Hybrid:</strong> Combines patterns for different entity requirements</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Confirmation Status */}
      {confirmedPlan && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-900">
              <p className="font-medium mb-1">Integration Plan Confirmed</p>
              <p>
                Your integration patterns have been saved. You can proceed to code generation
                or modify patterns as needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pattern Selector Component */}
      <div className="border rounded-lg bg-white">
        <IntegrationPatternSelector
          caseId={caseId}
          onConfirm={handleConfirm}
        />
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Data Flow
        </Button>
        <Button onClick={onNext}>
          Continue to Code Generation
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default Step7IntegrationPattern;
